package com.milta.feature.admin.domain.entity

data class AdminAccounts(
    val organisations: List<AdminOrganisation>,
    val independentDrivers: List<AdminAccount>,
)

data class AdminOrganisation(
    val enterpriseId: String,
    val name: String,
    val createdAt: String,
    val patron: AdminAccount,
    val members: List<AdminAccount>,
)

/** [joinedTeamAt] is the Membership's join date for the organisation this account is shown under
 * — null for a Patron entry and for an independent driver. */
data class AdminAccount(
    val accountId: String,
    val firstName: String,
    val lastName: String,
    val email: String?,
    val phoneNumber: String,
    val roles: List<String>,
    val joinedTeamAt: String?,
    val subscription: AdminSubscription?,
)

data class AdminSubscription(
    val plan: String,
    val status: String,
    val since: String?,
    val expiresAt: String?,
    val renewalCount: Int,
)

/** [ContractFooter] is always the server's locked constant — this entity never lets a caller
 * change it; only [body] can be edited/saved/reset. */
data class AdminPrompt(
    val key: String,
    val label: String,
    val endpoint: String,
    val body: String,
    val isOverridden: Boolean,
    val contractFooter: String,
    val updatedAt: String?,
    val updatedByAccountId: String?,
)

/** Background OCR analysis pipeline cost-comparison view (Dashboard Web "Usage IA") — real Claude
 * cost actually paid vs. what each of the 4 [AdminOcrAnalysisModeSummary] would have cost instead. */
data class AdminOcrAnalysis(
    val summary: AdminOcrAnalysisSummary,
    val modeSummaries: List<AdminOcrAnalysisModeSummary>,
)

/** [completedDocuments] is the subset with both pipeline halves written — only those are ever
 * simulated into [AdminOcrAnalysisModeSummary]. [documentsByFileKind] keys are FileKind.ToString()
 * ("Image"/"Pdf"/"Xlsx"), matching the backend enum serialization. */
data class AdminOcrAnalysisSummary(
    val totalDocuments: Int,
    val completedDocuments: Int,
    val totalRealCost: Double,
    val documentsByFileKind: Map<String, Int>,
    val totalInputTokens: Int,
    val totalOutputTokens: Int,
)

/** One row per PipelineMode ("Baseline"/"Minimal"/"TesseractGate"/"TesseractOnly", matching the
 * backend enum serialization). [totalRealCostForModeSet] is the real cost over the exact same
 * document subset this mode was simulated against — the apples-to-apples baseline for
 * [projectedSavings], not [AdminOcrAnalysisSummary.totalRealCost]. */
data class AdminOcrAnalysisModeSummary(
    val mode: String,
    val documentCount: Int,
    val claudeCallCount: Int,
    val totalProjectedCost: Double,
    val totalRealCostForModeSet: Double,
    val projectedSavings: Double,
)

/** Query-time period filter for "Conso. IA" — sent as-is (enum name) to the backend's
 * `?period=` query param, which parses it via standard ASP.NET Core enum model binding. */
enum class AdminClaudeUsagePeriod {
    Today,
    Last7Days,
    Last30Days,
    All,
}

/** Real production Claude token consumption per account/organisation (Dashboard Web "Conso. IA")
 * — distinct from [AdminOcrAnalysis]'s background cost-comparison experiment: every event here is
 * a real, billed call. [accounts] and the members embedded in [organisations] share the same shape;
 * an account outside any organisation only ever appears in [accounts]. */
data class AdminClaudeUsage(
    val summary: AdminClaudeUsageSummary,
    val accounts: List<AdminClaudeUsageAccount>,
    val organisations: List<AdminClaudeUsageOrganisation>,
)

data class AdminClaudeUsageSummary(
    val totalInputTokens: Long,
    val totalOutputTokens: Long,
    val totalCalls: Int,
    val failureRatePercent: Double,
    val activeAccountCount: Int,
    val activeOrganisationCount: Int,
    /** Indicative only — computed server-side from Anthropic's published per-model rate, never a
     * real billed amount (Anthropic's own invoice is the only source of truth for that). */
    val totalEstimatedCostEur: Double,
)

/** [tokensBySourceEndpoint] keys are ClaudeUsageSourceEndpoint.ToString() ("ScanDeliveryList" /
 * "ScanPackageLabel" / "ImportPdf" / "ImportUnstructured" / "SpreadsheetMapping"), matching the
 * backend enum serialization. [estimatedCostEur]/[averageEstimatedCostPerCallEur] are indicative
 * only — see [AdminClaudeUsageSummary.totalEstimatedCostEur]'s remarks. */
data class AdminClaudeUsageAccount(
    val accountId: String,
    val firstName: String,
    val lastName: String,
    val enterpriseId: String?,
    val enterpriseName: String?,
    val inputTokens: Long,
    val outputTokens: Long,
    val percentOfTotal: Double,
    val failureRatePercent: Double,
    val tokensBySourceEndpoint: Map<String, Long>,
    val estimatedCostEur: Double,
    val averageEstimatedCostPerCallEur: Double,
)

data class AdminClaudeUsageOrganisation(
    val enterpriseId: String,
    val enterpriseName: String,
    val inputTokens: Long,
    val outputTokens: Long,
    val percentOfTotal: Double,
    val failureRatePercent: Double,
    val tokensBySourceEndpoint: Map<String, Long>,
    val estimatedCostEur: Double,
    val averageEstimatedCostPerCallEur: Double,
    val members: List<AdminClaudeUsageAccount>,
)

/** One page of per-call detail behind an account row — pagination mandatory, same convention as
 * every other Admin/history list in this codebase (MIL-52's "Historique des preuves"). */
data class AdminClaudeUsageEvents(
    val totalCount: Int,
    val page: Int,
    val pageSize: Int,
    val events: List<AdminClaudeUsageEvent>,
)

/** [responseText] is the full, never-truncated text Claude returned — kept even on a Failed/
 * Unreadable [outcome] (a real call was still billed). [sourceFilePresignedUrl] is only non-null
 * when [hasSourceFile] and NOT [sourceFileExpired] — the uploaded file is auto-purged from MinIO
 * after 8 days, the ClaudeUsageEvent row and [responseText] survive regardless. */
data class AdminClaudeUsageEvent(
    val id: String,
    val createdAt: String,
    val sourceEndpoint: String,
    val inputTokens: Int,
    val outputTokens: Int,
    val outcome: String,
    val responseText: String?,
    val hasSourceFile: Boolean,
    val sourceFileExpired: Boolean,
    val sourceFilePresignedUrl: String?,
    /** Indicative only — see [AdminClaudeUsageSummary.totalEstimatedCostEur]'s remarks. */
    val estimatedCostEur: Double,
)
